import { JackpotTierType } from '@/components/jackpots-wrapper/types';
import { WidgetGradientClass } from '@/components/Widget/types';
import { CurrencyCodeType } from '@/types/currency';

export const JackpotProviders = {
  RedTiger: 'redtiger',
  BluePrint: 'blueprint',
  Section8: 'section8',
  GamesGlobal: 'gamesglobal',
} as const;

export type JackpotsSimplifiedType = Record<string, Record<string, any>>;

export type JackpotProvidersUnion = typeof JackpotProviders[keyof typeof JackpotProviders];

export interface JackpotTileStaticTexts {
  winMessage: string;
  nextJackpot: string;
  time: {
    hours: string;
    minutes: string;
    seconds: string;
  };
}
export interface JackpotTile {
  provider: JackpotProvidersUnion;
  logo: {
    source?: string;
    alt?: string;
  };
  background: {
    normal?: string;
    won?: string;
  };
  code: string;
  fields: {
    title?: string;
    code: string;
    messages?: {
      active?: string;
    };
    gradientClass?: {
      special?: string;
      normal?: string;
    };
  }[];
  staticTexts: JackpotTileStaticTexts;
  visibleFieldsAmount?: number;
}

export interface ExpectedTierType {
  name: string;
  state: 'normal' | 'active' | 'won';
  curValue: number;
  releaseTime?: string;
  releaseMessage?: string;
  previousPrice?: number;
  gradientClass?: WidgetGradientClass;
  type?: JackpotTierType;
}

interface OverlayTier {
  name: string;
  value: number;
  message?: string;
  nextValue: number;
  nextJackpotName: string;
  background: {
    source: {
      normal: string;
      retina?: string;
      modern?: {
        normal: string;
        retina?: string;
      };
    };
  };
}

export interface JackpotSlotMapped {
  type: JackpotProvidersUnion;
  images: {
    logo: {
      source: {
        normal: string;
        retina?: string;
        modern?: {
          normal: string;
          retina?: string;
        };
      };
      alt: string;
    };
    background: {
      source: {
        normal: string;
        retina?: string;
        modern?: {
          normal: string;
          retina?: string;
        };
      };
    };
  };
  staticTexts: {
    winMessage: string;
    nextJackpot: string;
    time: {
      hours: string;
      minutes: string;
      seconds: string;
    };
  };
  tiers: ExpectedTierType[];
  overlayTier?: OverlayTier;
  locale?: string;
  currencyCode?: string;
  timestamp: number;
}

export interface FetchJackpotsV2DataParams {
  currency?: string;
  gamesJackpotCodes: string[];
  provider: string;
}

export enum JackpotsV2StateValues {
  ACTIVE = 'ACTIVE',
  NORMAL = 'NORMAL',
  WON = 'WON',
}

export enum GamesGlobalJackpotV2Values {
  MEGA = 'MEGA',
  MAJOR = 'MAJOR',
  MINOR = 'MINOR',
  MINI = 'MINI',
}

export interface JackpotV2DataInterface {
  code: string;
  currency: CurrencyCodeType;
  timestamp: string;
}

export interface JackpotV2ValueInterface {
  dropFrom?: string;
  previousPrice?: number;
  price: number;
  state: JackpotsV2StateValues;
  targetTime?: string;
  updateInterval?: number;
  type?: JackpotTierType;
}

export interface SingleJackpotV2Interface extends JackpotV2DataInterface {
  value: JackpotV2ValueInterface;
}

export interface MultipleJackpotV2Interface extends JackpotV2DataInterface {
  values: Record<string, JackpotV2ValueInterface>;
}

export type JackpotsV2ResponseType = Record<string, SingleJackpotV2Interface>;

export type JackpotsV2MultipleResponseType = Record<
  string,
  MultipleJackpotV2Interface
>;

export type JackpotBetAmountsType = {
  type: 'ultra' | 'mega' | 'large' | 'big' | 'medium' | 'small';
  amount: number;
};

export type JackpotBetFeedsDataType = {
  timestamp: string;
  currency: string;
  winnersToday: number;
  winsToday: number;
  jpAmounts: JackpotBetAmountsType[];
};

export type JackpotsContext = Partial<
  Record<JackpotProvidersUnion, JackpotProviderState>
>;

export type JackpotProviderState = Record<string, JackpotCodeState>;

export type JackpotCodeState = {
  components: string[];
  fieldsValues?: MultipleJackpotV2Interface;
};

export type JackpotFeedsDataType = Record<string, MultipleJackpotV2Interface>;

export interface JackpotTier {
  title: string;
  value: number;
  currency?: CurrencyCodeType;
  shouldGlow: boolean;
  timestamp?: number; // Date.now() timestamp
}

export interface JackpotCountDownTier {
  title: string;
  targetTime: string;
}

export interface JackpotTiers {
  tiers: JackpotTier[];
  additionalTier?: JackpotTier;
  countDownTier?: JackpotCountDownTier;
}
// Perhaps types below should belong to some common content stack module
export type ContentStackFileType = ContentStackImageType; // Much more can be added as UNION, but for now we only use images
export type ContentStackImageType = 'image/png' | 'image/jpeg' | 'image/webp';

export interface JackpotLogo {
  normal: ContentStackFile<ContentStackImageType>;
  won?: ContentStackFile<ContentStackImageType>;
}
export interface ContentStackFile<T = ContentStackFileType>
  extends ContentStackEntry {
  url: string;
  content_type: T;
  file_size: string;
  filename: string;
  is_dir: boolean;
  parent_uid: string | null;
  children: Array<ContentStackEntryReference | ContentStackFile>;
}

export interface ContentStackEntry extends ContentStackEntryReference {
  created_at: string;
  created_by: string;
  updated_at: string;
  updated_by: string;
  title: string;
  tags: string[];
  _version: number;
}

export interface ContentStackEntryReference<T = string> {
  uid: string;
  content_type_uid: T;
}

export type ContentStackDate = string; // ISO 8601 format, e.g. "2025-07-15T08:37:50.000Z"
